'use client';
import { useState, useEffect, useCallback } from 'react';
export function useAuth({ authService, tokenKey = 'auth_token', onAuthChange, autoRefresh = true, refreshInterval = 15 * 60 * 1000, // 15 minutes
 }) {
    const [state, setState] = useState({
        user: null,
        isAuthenticated: false,
        isLoading: true,
        error: null,
    });
    const setError = useCallback((error) => {
        setState(prev => ({ ...prev, error, isLoading: false }));
    }, []);
    const setUser = useCallback((user) => {
        setState(prev => ({
            ...prev,
            user,
            isAuthenticated: !!user,
            isLoading: false,
            error: null,
        }));
        onAuthChange?.(user);
    }, [onAuthChange]);
    const login = useCallback(async (credentials) => {
        setState(prev => ({ ...prev, isLoading: true, error: null }));
        try {
            const { user, token } = await authService.login(credentials);
            localStorage.setItem(tokenKey, token);
            setUser(user);
            return { user, token };
        }
        catch (error) {
            const message = error instanceof Error ? error.message : 'Login failed';
            setError(message);
            throw error;
        }
    }, [authService, tokenKey, setUser, setError]);
    const logout = useCallback(async () => {
        setState(prev => ({ ...prev, isLoading: true, error: null }));
        try {
            await authService.logout();
            localStorage.removeItem(tokenKey);
            setUser(null);
        }
        catch (error) {
            const message = error instanceof Error ? error.message : 'Logout failed';
            setError(message);
            throw error;
        }
    }, [authService, tokenKey, setUser, setError]);
    const refreshUser = useCallback(async () => {
        const token = localStorage.getItem(tokenKey);
        if (!token) {
            setUser(null);
            return;
        }
        try {
            const user = await authService.verifyToken(token);
            setUser(user);
        }
        catch (error) {
            localStorage.removeItem(tokenKey);
            setUser(null);
        }
    }, [authService, tokenKey, setUser]);
    const clearError = useCallback(() => {
        setState(prev => ({ ...prev, error: null }));
    }, []);
    // Initialize auth state
    useEffect(() => {
        let mounted = true;
        const initAuth = async () => {
            const token = localStorage.getItem(tokenKey);
            if (!token) {
                if (mounted) {
                    setState(prev => ({ ...prev, isLoading: false }));
                }
                return;
            }
            try {
                const user = await authService.verifyToken(token);
                if (mounted) {
                    setUser(user);
                }
            }
            catch (error) {
                if (mounted) {
                    localStorage.removeItem(tokenKey);
                    setState(prev => ({ ...prev, isLoading: false }));
                }
            }
        };
        initAuth();
        return () => {
            mounted = false;
        };
    }, [authService, tokenKey, setUser]);
    // Auto refresh user data
    useEffect(() => {
        if (!autoRefresh || !state.isAuthenticated)
            return;
        const interval = setInterval(refreshUser, refreshInterval);
        return () => clearInterval(interval);
    }, [autoRefresh, state.isAuthenticated, refreshUser, refreshInterval]);
    return {
        ...state,
        login,
        logout,
        refreshUser,
        clearError,
    };
}
